import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Save, Plus, Edit2, Trash2 } from 'lucide-react';
import * as api from '../services/api';
import { useToast } from '../components/Toast';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';

export default function PermissionsPage() {
  const { t } = useTranslation();
  const [roles, setRoles] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ role: '', display_name: '', description: '', color: '#6B7280' });
  const [confirm, setConfirm] = useState({ isOpen: false });
  const toast = useToast();

  // Dynamiczne moduły — z ról (JSONB keys), nie hardcodowane
  const modules = useMemo(() => {
    if (availableModules.length > 0) return availableModules;
    const keys = new Set();
    roles.forEach(r => { if (r.permissions) Object.keys(r.permissions).forEach(k => keys.add(k)); });
    return [...keys].map(key => ({ key, name: key }));
  }, [roles, availableModules]);

  const permissions = ['view', 'create', 'edit', 'delete'];

  useEffect(() => { loadRoles(); }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const res = await api.getRoles();
      setRoles(res.data.roles || []);
      setAvailableModules(res.data.modules || []);
    } catch { toast.error(t('permissions.loadingError')); }
    finally { setLoading(false); }
  };

  const handleToggle = (roleIdx, modKey, perm) => {
    setRoles(prev => {
      const updated = [...prev];
      const role = { ...updated[roleIdx] };
      const perms = { ...(role.permissions || {}) };
      if (!perms[modKey]) perms[modKey] = {};
      perms[modKey] = { ...perms[modKey], [perm]: !perms[modKey][perm] };
      role.permissions = perms;
      updated[roleIdx] = role;
      return updated;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const role of roles) {
        if (role.is_system && role.role === 'admin') continue;
        await api.updateRole(role.id, { permissions: role.permissions });
      }
      toast.success(t('permissions.permissionsSaved'));
    } catch { toast.error(t('permissions.saveError')); }
    finally { setSaving(false); }
  };

  const handleSaveRole = async (e) => {
    e.preventDefault();
    try {
      if (editingRole) {
        await api.updateRole(editingRole.id, roleForm);
      } else {
        await api.createRole(roleForm);
      }
      toast.success(t('permissions.roleSaved'));
      setShowModal(false);
      loadRoles();
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
  };

  const handleDeleteRole = (role) => {
    if (role.is_system) return toast.error(t('permissions.cannotDeleteSystemRole'));
    setConfirm({
      isOpen: true, title: t('permissions.deleteRole'),
      message: t('permissions.deleteRoleConfirm', { name: role.display_name }),
      onConfirm: async () => {
        try { await api.deleteRole(role.id); toast.success(t('common.deleted')); loadRoles(); }
        catch { toast.error(t('common.error')); }
        finally { setConfirm({ isOpen: false }); }
      }
    });
  };

  if (loading) return <div className="p-6 text-center text-gray-400">{t('common.loading')}</div>;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Shield size={24} className="text-indigo-600" />
          <h1 className="text-xl font-bold text-gray-800">{t('permissions.title')}</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setEditingRole(null); setRoleForm({ role: '', display_name: '', description: '', color: '#6B7280' }); setShowModal(true); }}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm">
            <Plus size={16} /> {t('permissions.newRole')}
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm disabled:opacity-50">
            <Save size={16} /> {saving ? t('permissions.saving') : t('common.save')}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left p-3 font-medium text-gray-600 sticky left-0 bg-gray-50">{t('permissions.module')}</th>
              {roles.map(r => (
                <th key={r.id} className="text-center p-3 font-medium min-w-[100px]" colSpan={permissions.length}>
                  <div className="flex items-center justify-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: r.color }} />
                    <span>{r.display_name}</span>
                    {!r.is_system && (
                      <>
                        <button onClick={() => { setEditingRole(r); setRoleForm({ display_name: r.display_name, description: r.description, color: r.color }); setShowModal(true); }}
                          className="opacity-40 hover:opacity-100"><Edit2 size={12} /></button>
                        <button onClick={() => handleDeleteRole(r)} className="opacity-40 hover:opacity-100"><Trash2 size={12} /></button>
                      </>
                    )}
                  </div>
                </th>
              ))}
            </tr>
            <tr className="border-b">
              <th className="sticky left-0 bg-gray-50" />
              {roles.map(r => permissions.map(p => (
                <th key={`${r.id}-${p}`} className="text-center px-1 py-1 text-xs text-gray-400 font-normal">
                  {p === 'view' ? 'P' : p === 'create' ? 'D' : p === 'edit' ? 'E' : 'U'}
                </th>
              )))}
            </tr>
          </thead>
          <tbody>
            {modules.map(mod => (
              <tr key={mod.key} className="border-b hover:bg-gray-50">
                <td className="p-3 font-medium text-gray-700 sticky left-0 bg-white">{mod.name}</td>
                {roles.map((r, ri) => permissions.map(p => (
                  <td key={`${r.id}-${mod.key}-${p}`} className="text-center px-1 py-2">
                    {r.is_system && r.role === 'admin' ? (
                      <span className="text-green-500 text-xs">*</span>
                    ) : (
                      <input type="checkbox"
                        checked={r.permissions?.[mod.key]?.[p] || false}
                        onChange={() => handleToggle(ri, mod.key, p)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    )}
                  </td>
                )))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="p-2 text-xs text-gray-400 border-t">{t('permissions.legend')}</div>
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingRole ? t('permissions.editRole') : t('permissions.newRole')}>
        <form onSubmit={handleSaveRole} className="space-y-4">
          {!editingRole && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('permissions.keyLabel')}</label>
              <input value={roleForm.role} onChange={e => setRoleForm({ ...roleForm, role: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg" pattern="[a-z][a-z0-9_]*" required />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.name')}</label>
            <input value={roleForm.display_name} onChange={e => setRoleForm({ ...roleForm, display_name: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')}</label>
            <input value={roleForm.description || ''} onChange={e => setRoleForm({ ...roleForm, description: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('permissions.color')}</label>
            <input type="color" value={roleForm.color} onChange={e => setRoleForm({ ...roleForm, color: e.target.value })}
              className="w-12 h-8 border rounded cursor-pointer" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">{t('common.cancel')}</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">{t('common.save')}</button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog isOpen={confirm.isOpen} title={confirm.title} message={confirm.message}
        onConfirm={confirm.onConfirm} onCancel={() => setConfirm({ isOpen: false })} type="danger" />
    </div>
  );
}
